import {
    Box,
    Grid,
    IconButton,
    MapTooltip,
    Popper,
    Stack,
    TextField,
    Typography,
} from "@biggeo/bg-ui/lab";
import {
    CloseOutline,
    EditOutline,
    FunctionOutline,
    SearchOutline,
} from "@biggeo/bg-ui/lab/icons";
import React, { useState } from "react";

import { useCreateSelectedShapeFunctionTooltip } from "../../map-tooltip/redux/hooks";
import { PolygonFeature } from "../../utils/type";
import { ExpandRegionFunctionItem } from "../components/ExpandRegionFunction";
import { useMap } from "../context";
import { isPolygonFeature } from "../../utils/converters";
import { MapContextPolygon } from "../context/types";
import { usePolygon } from "../context/hooks";

enum AvailableFunctions {
    expandRegion = "expandRegion",
}

export const SelectedShapeFunctions = ({
    onClickEdit,
}: {
    readonly onClickEdit?: () => void;
}) => {
    const { polygons } = useMap();

    return polygons
        .filter((p) => !!p.isSelected)
        .map((polygon, index) => {
            if (isPolygonFeature(polygon.feature)) {
                return (
                    <PolygonFunctionsTooltip
                        key={polygon.id}
                        defaultShapeName={`Shape #${index + 1}`}
                        selectedFeature={polygon.feature}
                        matchingPolygon={polygon}
                        onClickEdit={onClickEdit}
                    />
                );
            }
        });
};

const PolygonFunctionsTooltip = ({
    defaultShapeName,
    selectedFeature,
    matchingPolygon,
    onClickEdit,
}: {
    readonly defaultShapeName: string;
    readonly selectedFeature: PolygonFeature;
    readonly matchingPolygon: MapContextPolygon;
    readonly onClickEdit?: () => void;
}) => {
    const tooltipRef = React.useRef<HTMLDivElement>(null);

    return (
        // Wrapping this in a Box is necessary, otherwise it throws a Node error when removing the tooltip
        <Box>
            <MapTooltip
                ref={tooltipRef}
                tooltipId="selected-shape"
                disableArrow
                disableDisplayCondition
            >
                <SelectedShapeFunctionsPopper
                    defaultShapeName={defaultShapeName}
                    tooltipRef={tooltipRef}
                    selectedFeature={selectedFeature}
                    matchingPolygon={matchingPolygon}
                    onClickEdit={onClickEdit}
                />
            </MapTooltip>
        </Box>
    );
};

const SelectedShapeFunctionsPopper = ({
    defaultShapeName,
    tooltipRef,
    selectedFeature,
    matchingPolygon,
    onClickEdit,
}: {
    readonly defaultShapeName: string;
    readonly tooltipRef: React.RefObject<HTMLDivElement>;
    readonly selectedFeature: PolygonFeature;
    readonly matchingPolygon: MapContextPolygon;
    readonly onClickEdit?: () => void;
}) => {
    const { removeMarker } = useCreateSelectedShapeFunctionTooltip(
        selectedFeature,
        matchingPolygon.id,
        tooltipRef
    );

    const name = selectedFeature.properties?.name;

    const { updatePolygon } = usePolygon();

    return (
        <Grid container gap={2}>
            <Grid
                item
                sx={{
                    backgroundColor: (theme) => theme.palette.background.main,
                    padding: 2,
                    borderRadius: 1,
                    borderColor: (theme) => theme.palette.stroke[100],
                }}
            >
                <Grid
                    container
                    gap={1}
                    justifyContent="space-between"
                    alignItems="center"
                    height="100%"
                >
                    <Grid item>
                        <Typography variant="body3">
                            {!!name && typeof name === "string"
                                ? name
                                : defaultShapeName}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Grid container gap={1}>
                            {onClickEdit && (
                                <IconButton
                                    variant="ghost"
                                    density="dense"
                                    onClick={onClickEdit}
                                >
                                    <EditOutline size="sm" />
                                </IconButton>
                            )}
                            <SelectedShapeFunctionsMenu
                                originalPolygon={matchingPolygon}
                                selectedFeature={selectedFeature}
                                removeSelectedShape={(id) =>
                                    updatePolygon({ id, isSelected: false })
                                }
                            />
                            <IconButton
                                variant="ghost"
                                density="dense"
                                onClick={removeMarker}
                            >
                                <CloseOutline size="sm" />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

const SelectedShapeFunctionsMenu = ({
    originalPolygon,
    selectedFeature,
    removeSelectedShape,
}: {
    readonly originalPolygon: MapContextPolygon;
    readonly selectedFeature: PolygonFeature;
    readonly removeSelectedShape: (id: string) => void;
}) => {
    const [searchValue, setSearchValue] = useState<string>();
    const [open, setOpen] = useState(false);

    const availableFunctions: {
        [K in AvailableFunctions]: JSX.Element;
    } = {
        expandRegion: (
            <ExpandRegionFunctionItem
                originalPolygonId={originalPolygon.id}
                selectedFeature={selectedFeature}
                onClose={() => {
                    removeSelectedShape(originalPolygon.id);
                    setOpen(false);
                }}
            />
        ),
    };

    /**
     * Filters and returns available functions based on search value.
     * If no search value is provided, returns all available functions.
     * If search value exists, filters functions by matching the function key (case-insensitive).
     */
    const getAvailableFunctionsFromSearch = (): readonly JSX.Element[] => {
        if (!searchValue) {
            return Object.values(availableFunctions);
        }

        return Object.entries(availableFunctions)
            .filter(([key]) =>
                key.toLowerCase().includes(searchValue.toLowerCase())
            )
            .map(([_, value]) => value);
    };

    return (
        <Popper
            open={open}
            onOpenChange={setOpen}
            placement="bottom-start"
            content={
                <Stack rowGap={1}>
                    <TextField
                        startNode={<SearchOutline size="xs" />}
                        placeholder="Search"
                        fullWidth
                        value={searchValue}
                        onChange={(_, v) => {
                            setSearchValue(v);
                        }}
                    />
                    {...getAvailableFunctionsFromSearch()}
                </Stack>
            }
            configuration={{
                openOnHover: false,
                preventCloseOnHoverAway: true,
                preventCloseOnHoverBetweenTarget: true,
            }}
            removeFloatingArrow
            sx={{
                width: (theme) => theme.spacing(52.5),
                padding: 2,
            }}
        >
            <IconButton variant="ghost" density="dense">
                <FunctionOutline size="sm" />
            </IconButton>
        </Popper>
    );
};
